import React, { useCallback, useEffect, useRef, useState } from "react";
import Pagination from "@mui/material/Pagination";
import { API_URLS } from "../../../../apiUrl";
import axiosInstance from "../../../../components/axiosInstance";
import { toast, ToastContainer } from "react-toastify";
import CustomSelect from "../../../../components/CustomSelect";

// ─── Interfaces ───────────────────────────────────────────────────────────────
interface Collector {
  primary_id: number;
  municipality_name: string;
  municipality_id: number;
  login_user_name: string;
  admin_name: string;
  email: string | null;
  role_id: number;
  role_name: string;
  role_type: string;
  status: number;
  ward_id: number[] | number | null;
  ward_no: string | null;
  ward_name?: string | null;
  ward_data?: Array<{ ward_id: number; ward_name: string }>;
  borough_name: string | null;
  created_at: string;
  modified_at: string;
  is_deleted: boolean;
  logo?: string | null;
  banner_image?: string | null;
  municipalty_chairman_person_name?: string;
  municipalty_offical_address?: string;
  gst?: string;
  tan?: string;
  pan?: string;
  state_id?: number;
  city_id?: number;
  street_id?: number;
  ward_no_alt?: any;
  borough_name_alt?: any;
}

interface RoleType {
  primary_role_id: number;
  role_name: string;
  status: number;
}

interface Borough {
  borough_primary_id: number | string;
  borough_name: string;
}

interface Ward {
  id: number;
  name: string;
}

interface MunicipalityType {
  primary_id: number;
  municipality_name: string;
}

interface SelectOptionType {
  value: string;
  label: string;
}

interface CollectorListProps {
  onViewDetails: (collector: Collector) => void;
}

// ─── Ward Popup Modal Component ───────────────────────────────────────────────
const WardPopupModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  wardNames: string[];
  collectorName: string;
}> = ({ isOpen, onClose, wardNames, collectorName }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Assigned Wards</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              {collectorName} • {wardNames.length} ward{wardNames.length !== 1 ? 's' : ''}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body with ward tags */}
        <div className="px-6 py-5">
          {wardNames.length === 0 ? (
            <p className="text-center text-gray-500 py-4">No wards assigned</p>
          ) : (
            <div className="flex flex-wrap gap-2 max-h-96 overflow-y-auto">
              {wardNames.map((name, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center gap-2 bg-blue-50 border border-blue-200 text-blue-700 text-sm font-medium px-3 py-1.5 rounded-full"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  {name}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Ward Display Component for Table ────────────────────────────────────────
const WardDisplay: React.FC<{ collector: Collector }> = ({ collector }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const wardNames: string[] = collector.ward_data 
    ? collector.ward_data.map(w => w.ward_name)
    : collector.ward_no 
      ? collector.ward_no.split(',').map(w => w.trim())
      : [];
  
  const hasMultipleWards = wardNames.length > 1;
  
  if (wardNames.length === 0) {
    return <span className="text-gray-400">N/A</span>;
  }

  if (!hasMultipleWards) {
    return (
      <div className="max-w-[90px]">
        <span className="block truncate" title={wardNames[0]}>
          {wardNames[0]}
        </span>
      </div>
    );
  }

  return (
    <>
      <div className="inline-flex items-center gap-1 max-w-[90px] w-full">
        <span className="truncate flex-1 min-w-0" title={wardNames[0]}>
          {wardNames[0]}
        </span>
        <span className="inline-flex items-center justify-center bg-blue-100 text-blue-700 text-xs px-1.5 py-0.5 rounded-full flex-shrink-0">
          +{wardNames.length - 1}
        </span>
        <button
          onClick={() => setIsModalOpen(true)}
          className="w-5 h-5 rounded-full bg-blue-100 hover:bg-blue-200 text-blue-600 hover:text-blue-700 flex items-center justify-center transition-colors flex-shrink-0"
          title={`View all ${wardNames.length} wards`}
        >
          <i className="ri-information-line text-xs"></i>
        </button>
      </div>
      
      <WardPopupModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        wardNames={wardNames}
        collectorName={collector.admin_name}
      />
    </>
  );
};

// ─── Ward Tag Multi-Select ────────────────────────────────────────────────────
const WardTagSelect: React.FC<{
  selectedIds: string[];
  options: { label: string; value: string }[];
  placeholder: string;
  disabled?: boolean;
  onChange: (values: string[]) => void;
}> = ({ selectedIds, options, placeholder, disabled, onChange }) => {
  const [search, setSearch] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const unselected = options.filter((o) => !selectedIds.includes(o.value));
  const filtered = search.trim()
    ? unselected.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()))
    : unselected;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setShowSuggestions(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const addWard = (value: string) => {
    if (!selectedIds.includes(value)) onChange([...selectedIds, value]);
    setSearch("");
    inputRef.current?.focus();
  };

  const removeWard = (value: string) => {
    onChange(selectedIds.filter((id) => id !== value));
  };

  const selectedLabels = selectedIds.map((id) => ({
    value: id,
    label: options.find((o) => o.value === id)?.label ?? id,
  }));

  return (
    <div ref={ref} className="w-full">
      <div
        onClick={() => { if (!disabled) { setShowSuggestions(true); inputRef.current?.focus(); } }}
        className={`min-h-[44px] flex flex-wrap gap-1.5 items-center border rounded-lg px-2.5 py-2 cursor-text transition-all ${
          disabled
            ? "bg-gray-50 border-gray-200 cursor-not-allowed"
            : showSuggestions
            ? "border-blue-500 ring-2 ring-blue-100 bg-white"
            : "border-gray-200 bg-white hover:border-gray-400"
        }`}
      >
        {selectedLabels.map((item) => (
          <span key={item.value} className="inline-flex items-center gap-1 bg-blue-50 border border-blue-200 text-blue-700 text-xs font-medium px-2.5 py-1 rounded-full">
            {item.label}
            {!disabled && (
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); removeWard(item.value); }}
                className="ml-0.5 w-3.5 h-3.5 rounded-full bg-blue-200 hover:bg-blue-500 hover:text-white text-blue-600 flex items-center justify-center flex-shrink-0 transition-colors"
              >
                <svg viewBox="0 0 10 10" className="w-2 h-2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M2 2l6 6M8 2l-6 6" />
                </svg>
              </button>
            )}
          </span>
        ))}
        {!disabled && (
          <input
            ref={inputRef}
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setShowSuggestions(true); }}
            onFocus={() => setShowSuggestions(true)}
            placeholder={selectedIds.length === 0 ? placeholder : "Add more..."}
            className="flex-1 min-w-[120px] text-sm outline-none bg-transparent text-gray-700 placeholder-gray-400 py-0.5"
          />
        )}
        {disabled && selectedIds.length === 0 && (
          <span className="text-sm text-gray-400 px-1">{placeholder}</span>
        )}
      </div>
      {showSuggestions && !disabled && (
        <div className="relative">
          <ul className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-xl max-h-48 overflow-y-auto">
            {filtered.length > 0 ? (
              filtered.map((o) => (
                <li
                  key={o.value}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => addWard(o.value)}
                  className="px-3 py-2.5 text-sm cursor-pointer hover:bg-blue-50 text-gray-700 hover:text-blue-700 transition-colors flex items-center gap-2.5"
                >
                  <span className="w-4 h-4 rounded border border-gray-300 flex-shrink-0 bg-white" />
                  {o.label}
                </li>
              ))
            ) : (
              <li className="px-3 py-3 text-sm text-gray-400 text-center">
                {search.trim() ? "No matching wards found" : unselected.length === 0 ? "All wards selected" : "Type to search wards..."}
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

// ─── Edit Modal ───────────────────────────────────────────────────────────────
interface EditModalProps {
  collector: Collector;
  roleTypes: RoleType[];
  municipaltyData: string;
  onClose: () => void;
  onUpdated: () => void;
}

const EditModal: React.FC<EditModalProps> = ({ collector, roleTypes, onClose, onUpdated, municipaltyData }) => {
  const MunicipalityID = (() => {
    const storedId = localStorage.getItem("municipality_id_v2");
    if (storedId && storedId !== "null" && storedId !== "undefined" && storedId !== "") {
      return storedId;
    }
    return municipaltyData || "";
  })();

  const getInitialWards = (): string[] => {
    if (collector.ward_data && collector.ward_data.length > 0) {
      return collector.ward_data.map(w => String(w.ward_id));
    }
    if (collector.ward_id) {
      if (Array.isArray(collector.ward_id)) {
        return collector.ward_id.map(id => String(id));
      }
      return [String(collector.ward_id)];
    }
    return [];
  };

  const [form, setForm] = useState({
    admin_name: collector.admin_name ?? "",
    login_user_name: collector.login_user_name ?? "",
    email: collector.email ?? "",
    role_id: String(collector.role_id ?? ""),
    status: collector.status ?? 1,
    password: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [selectedRole, setSelectedRole] = useState<string>(collector.role_name ?? "");
  const [boroughs, setBoroughs] = useState<Borough[]>([]);
  const [loadingBorough, setLoadingBorough] = useState(false);
  const [selectedBorough, setSelectedBorough] = useState<string>("");
  const [wards, setWards] = useState<Ward[]>([]);
  const [loadingWard, setLoadingWard] = useState(false);
  const [selectedWards, setSelectedWards] = useState<string[]>(getInitialWards());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [hasLoadedBoroughs, setHasLoadedBoroughs] = useState(false);
  const [hasFetchedBoroughs, setHasFetchedBoroughs] = useState(false);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const emailValid = !form.email || emailRegex.test(form.email);
  const currentIsField = selectedRole === "collector";

  const passwordChecks = {
    length: form.password.length >= 6,
    uppercase: /[A-Z]/.test(form.password),
    number: /\d/.test(form.password),
  };
  const passwordValid = form.password === "" || Object.values(passwordChecks).every(Boolean);

  useEffect(() => {
    if (collector.borough_name && boroughs.length > 0 && !selectedBorough && !hasLoadedBoroughs) {
      const matched = boroughs.find(
        (b) => String(b.borough_name).toLowerCase() === String(collector.borough_name).toLowerCase()
      );
      if (matched) {
        setSelectedBorough(String(matched.borough_primary_id));
        setHasLoadedBoroughs(true);
      }
    }
  }, [boroughs, collector.borough_name, selectedBorough, hasLoadedBoroughs]);

  useEffect(() => {
    if (!currentIsField) return;
    
    if (!hasFetchedBoroughs && !loadingBorough) {
      setHasFetchedBoroughs(true);
      setLoadingBorough(true);
      axiosInstance
        .post(API_URLS.GetMunicipalityBorough, {
          municipalityTbl_id: MunicipalityID, page: 1, limit: 500, surveyer_id: 0,
        })
        .then((res) => {
          const list: Borough[] = res.data?.data?.data ?? [];
          setBoroughs(list);
        })
        .catch((err: any) => {
          toast.error(err?.response?.data?.message ?? err?.message ?? "Failed to load boroughs", { position: "top-right", autoClose: 3000 });
          setHasFetchedBoroughs(false);
        })
        .finally(() => setLoadingBorough(false));
    }
  }, [currentIsField, hasFetchedBoroughs, loadingBorough, MunicipalityID]);

  useEffect(() => {
    if (!selectedBorough) return;
    setWards([]);
    setLoadingWard(true);
    axiosInstance
      .post(API_URLS.GetBoroughWiseWard, {
        municipalityTbl_id: MunicipalityID, borough_primary_id: selectedBorough, page: 1, limit: 50, surveyer_id: 0,
      })
      .then((res) => {
        const wardList: Ward[] = res.data?.data?.data?.map((w: any) => ({
          id: w.ward_primary_id, name: w.ward_no,
        })) ?? [];
        setWards(wardList);
      })
      .catch((err: any) => {
        toast.error(err?.response?.data?.message ?? err?.message ?? "Failed to load wards", { position: "top-right", autoClose: 3000 });
      })
      .finally(() => setLoadingWard(false));
  }, [selectedBorough, MunicipalityID]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    if (e.target.name === "email") setEmailTouched(true);
    if (e.target.name === "password") setPasswordTouched(true);
  };

  const handleUpdate = async () => {
    if (isSubmittingRef.current) return;
    if (!form.admin_name.trim()) {
      toast.warn("Please fill in Full Name", { position: "top-right", autoClose: 3000 }); return;
    }
    if (!form.login_user_name.trim()) {
      toast.warn("Please fill in Username", { position: "top-right", autoClose: 3000 }); return;
    }
    if (form.email && !emailValid) {
      setEmailTouched(true);
      toast.warn("Please enter a valid email address", { position: "top-right", autoClose: 3000 }); return;
    }
    if (form.password && !passwordValid) {
      setPasswordTouched(true);
      toast.warn("Password must be at least 6 characters, include 1 uppercase and 1 number", { position: "top-right", autoClose: 3000 }); return;
    }
    if (currentIsField && !selectedBorough) {
      toast.warn("Please select a Borough", { position: "top-right", autoClose: 3000 }); return;
    }
    if (currentIsField && selectedWards.length === 0) {
      toast.warn("Please select at least one Ward", { position: "top-right", autoClose: 3000 }); return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    try {
      const payload: any = {
        id: collector.primary_id,
        login_user_name: form.login_user_name,
        admin_name: form.admin_name,
        email: form.email,
        role_id: form.role_id,
        ward_id: currentIsField ? selectedWards : [],      
        borough_primary_id: currentIsField ? selectedBorough : "",
        municipality_id: collector.municipality_id
      };
      if (form.password.trim()) payload.password = form.password;

      const response = await axiosInstance.post(API_URLS.updateCashierandcollector, payload);
      toast.success(response?.data?.message || "Updated successfully!", {
        position: "top-right",
        autoClose: 3000
      });
      onUpdated();
      onClose();
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? err?.response?.data?.error ?? err?.message ?? "Update failed.";
      toast.error(msg, { position: "top-right", autoClose: 4000 });
    } finally {
      setTimeout(() => { isSubmittingRef.current = false; setIsSubmitting(false); }, 3000);
    }
  };

  const boroughOptions = boroughs.map((b) => ({ label: b.borough_name, value: String(b.borough_primary_id) }));
  const wardOptions = wards.map((w) => ({ label: w.name, value: String(w.id) }));

  const inputCls = (name: string) => {
    const base = "w-full border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 transition-colors bg-white";
    if (name === "email" && emailTouched && form.email)
      return emailValid ? `${base} border-green-400 focus:ring-green-100` : `${base} border-red-400 focus:ring-red-100`;
    if (name === "password" && passwordTouched && form.password)
      return passwordValid ? `${base} border-green-400 focus:ring-green-100` : `${base} border-red-400 focus:ring-red-100`;
    return `${base} border-gray-200 focus:ring-blue-100 focus:border-blue-400`;
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(15,23,42,0.5)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full flex flex-col"
        style={{ maxWidth: 820, maxHeight: "92vh" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-5 border-b border-gray-100">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0 border border-blue-200">
              <span className="text-blue-700 font-bold text-base">
                {collector.admin_name?.charAt(0)?.toUpperCase() ?? "C"}
              </span>
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900 leading-tight">Edit Collector</h2>
              <p className="text-xs text-gray-400 mt-0.5">
                {collector.municipality_name}&nbsp;&middot;&nbsp;ID #{collector.primary_id}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${
              currentIsField
                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : "bg-blue-50 text-blue-700 border-blue-200"
            }`}>
              {currentIsField ? "Field Collector" : "Counter Collector"}
            </span>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Scrollable Body */}
        <div className="flex-1 overflow-y-auto px-8 py-6 space-y-7">
          {/* Section 1: Assignment */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-1 h-4 rounded-full bg-blue-500" />
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest">Assignment</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1.5">Borough</label>
                <CustomSelect
                  name="borough"
                  value={selectedBorough}
                  options={boroughOptions}
                  placeholder={!currentIsField ? "— Not applicable —" : loadingBorough ? "Loading..." : "Select Borough"}
                  disabled={!currentIsField || loadingBorough}
                  onChange={(e: any) => { setSelectedBorough(e.target.value); setSelectedWards([]); }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1.5 flex items-center gap-2">
                  Ward
                  {selectedWards.length > 0 && (
                    <span className="text-xs font-normal bg-blue-50 text-blue-600 border border-blue-200 px-1.5 py-0.5 rounded-full">
                      {selectedWards.length} selected
                    </span>
                  )}
                </label>
                <WardTagSelect
                  selectedIds={selectedWards}
                  options={wardOptions}
                  placeholder={
                    !currentIsField ? "N/A for counter collector"
                    : !selectedBorough ? "Select borough first..."
                    : loadingWard ? "Loading wards..."
                    : "Type to search ward..."
                  }
                  disabled={!currentIsField || !selectedBorough || loadingWard}
                  onChange={setSelectedWards}
                />
              </div>
            </div>
            {currentIsField && !selectedBorough && (
              <p className="text-xs text-amber-600 mt-2.5 flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                Please select a Borough to load wards
              </p>
            )}
            {currentIsField && selectedBorough && selectedWards.length === 0 && (
              <p className="text-xs text-amber-600 mt-2.5 flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                Type ward name to search and select (multiple allowed)
              </p>
            )}
          </div>

          <div className="border-t border-dashed border-gray-200" />

          {/* Section 2: Personal Details */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-1 h-4 rounded-full bg-indigo-500" />
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest">Personal Details</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1.5">
                  Full Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text" name="admin_name" value={form.admin_name}
                  onChange={handleChange} placeholder="e.g. Rahul Kumar"
                  className={inputCls("admin_name")} maxLength={100}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1.5">
                  Username <span className="text-red-400">*</span>
                </label>
                <input
                  type="text" name="login_user_name" value={form.login_user_name}
                  onChange={handleChange} placeholder="e.g. rahul_tmr"
                  className={inputCls("login_user_name")} maxLength={100}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1.5">
                  Email <span className="text-gray-400 font-normal text-xs">(optional)</span>
                </label>
                <input
                  type="email" name="email" value={form.email}
                  onChange={handleChange} onBlur={() => setEmailTouched(true)}
                  placeholder="e.g. rahul@gmail.com"
                  className={inputCls("email")} maxLength={100}
                />
                {emailTouched && form.email && !emailValid && (
                  <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1.5">
                    <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Please enter a valid email address
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1.5">
                  New Password <span className="text-gray-400 font-normal text-xs">(optional)</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={form.password}
                    onChange={handleChange}
                    onBlur={() => { if (form.password) setPasswordTouched(true); }}
                    placeholder="Leave blank to keep current"
                    className={inputCls("password")}
                    maxLength={20}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((p) => !p)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? (
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 4.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                {passwordTouched && form.password && (
                  <div className="mt-2.5 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-xs font-medium text-gray-500 mb-2">Password requirements:</p>
                    <div className="flex flex-col gap-1.5">
                      {[
                        { key: "length", label: "At least 6 characters" },
                        { key: "uppercase", label: "At least 1 uppercase letter (A-Z)" },
                        { key: "number", label: "At least 1 number (0-9)" },
                      ].map(({ key, label }) => (
                        <div key={key} className="flex items-center gap-2">
                          <span className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${
                            passwordChecks[key as keyof typeof passwordChecks] ? "bg-green-500" : "bg-gray-300"
                          }`}>
                            {passwordChecks[key as keyof typeof passwordChecks] && (
                              <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 10 10" fill="none">
                                <path d="M1.5 5L4 7.5L8.5 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            )}
                          </span>
                          <span className={`text-xs ${passwordChecks[key as keyof typeof passwordChecks] ? "text-green-600" : "text-gray-400"}`}>
                            {label}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="border-t border-dashed border-gray-200" />

          <div>
            <div className="mt-4 bg-slate-50 rounded-xl border border-slate-100 px-6 py-4 grid grid-cols-2 md:grid-cols-3 gap-5">
              {[
                { label: "Municipality", value: collector.municipality_name },
                { label: "Current Ward", value: collector.ward_no ?? "N/A" },
                { label: "Current Borough", value: collector.borough_name ?? "N/A" },
              ].map((item) => (
                <div key={item.label}>
                  <p className="text-xs text-gray-400 font-medium mb-1">{item.label}</p>
                  <p className="text-sm text-gray-700 font-medium truncate" title={item.value}>{item.value}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-8 py-4 border-t border-gray-100 bg-gray-50/80 rounded-b-2xl">
          <p className="text-xs text-gray-400">
            <span className="text-red-400">*</span> Required fields
          </p>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2.5 rounded-lg text-sm font-medium text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleUpdate}
              disabled={isSubmitting}
              className="px-7 py-2.5 rounded-lg text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2 min-w-[150px] justify-center"
            >
              {isSubmitting ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  Updating...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  Update Collector
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main List Component ───────────────────────────────────────────────────────
const CollectorList: React.FC<CollectorListProps> = ({ onViewDetails }) => {
  const [collectorList, setCollectorList] = useState<Collector[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [roleTypes, setRoleTypes] = useState<RoleType[]>([]);
  const [selectedRole, setSelectedRole] = useState<string>("collector");
  const [totalPages, setTotalPages] = useState(1);
  const [displayList, setDisplayList] = useState<Collector[]>([]);
  const [editingCollector, setEditingCollector] = useState<Collector | null>(null);
  const [municipalty, setMunicipalty] = useState<string>(() => {
    const stored = localStorage.getItem("selected_collector_municipality");
    return stored || "";
  });
  const [fetchMunicipalty, setfetchMunicipalty] = useState<MunicipalityType[]>([]);
  const [loading, setLoading] = useState(false);

  const LIMIT = 50;
  const role_name = localStorage.getItem('role_name');

  useEffect(() => {
    if (municipalty) {
      localStorage.setItem("selected_collector_municipality", municipalty);
    }
  }, [municipalty]);

  const fetchCollectors = useCallback(async () => {
    if (role_name === "super admin" && !municipalty) {
      setCollectorList([]);
      setDisplayList([]);
      return;
    }
    
    if (!selectedRole) { 
      setCollectorList([]); 
      setDisplayList([]); 
      return; 
    }

    setLoading(true);
    try {
      const response = await axiosInstance.post(`${API_URLS.usercollector}`, {
        role_name: selectedRole,
        limit: LIMIT,
        page,
        ...(role_name === "super admin" && municipalty ? { municipality_id: String(municipalty) } : {}),
      });
      const rows: Collector[] = response.data.data.rows ?? [];
      const pagination = response.data.data.pagination;
      setCollectorList(rows);
      setDisplayList(rows);
      setTotalPages(pagination?.lastPage ?? 1);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ?? error?.message ?? "Failed to fetch collectors",
        { position: "top-right", autoClose: 3000 }
      );
    } finally {
      setLoading(false);
    }
  }, [selectedRole, page, municipalty]);

  useEffect(() => { fetchCollectors(); }, [fetchCollectors]);

  useEffect(() => {
    if (!searchQuery.trim()) { 
      setDisplayList(collectorList); 
      return; 
    }
    const t = setTimeout(async () => {
      try {
        const response = await axiosInstance.post(API_URLS.usercollector, {
          role_name: selectedRole, 
          search: searchQuery.trim(), 
          limit: LIMIT, 
          page: 1,
          ...(role_name === "super admin" && municipalty ? { municipality_id: String(municipalty) } : {}),
        });
        setDisplayList(response.data.data.rows ?? []);
      } catch (error: any) {
        toast.error(error?.message ?? "Search failed", { position: "top-right", autoClose: 3000 });
      }
    }, 500);
    return () => clearTimeout(t);
  }, [searchQuery, selectedRole, collectorList, municipalty]);

  useEffect(() => {
    axiosInstance
      .get(API_URLS.roleType, { params: { role_type: "collector" } })
      .then((res) => setRoleTypes(res.data?.data ?? []))
      .catch((err: any) => {
        toast.error(err?.message ?? "Failed to load role types", { position: "top-right", autoClose: 3000 });
      });
  }, []);

  useEffect(() => {
    if (editingCollector) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [editingCollector]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const responce = await axiosInstance.post(API_URLS.MunicipalityList, {
          limit: 150,
          page: 1
        });

        const raw = responce.data?.data;

        const list: MunicipalityType[] = Array.isArray(raw)
          ? raw
          : raw?.rows ?? raw?.data ?? [];

        setfetchMunicipalty(list);
      } catch (err: any) {
        toast.error(
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message
        );
        console.error("Municipality fetch error:", err);
      }
    };

    fetchData();
  }, []);

  // FIX: Update municipality and persist to localStorage
  const handleMunicipalityChange = (value: string) => {
    setMunicipalty(value);
    localStorage.setItem("selected_collector_municipality", value);
    setPage(1);
    setSearchQuery("");
  };

  const municipalityOptions: SelectOptionType[] = fetchMunicipalty.map((c) => ({
    value: String(c.primary_id),
    label: c.municipality_name,
  }));

  return (
    <div className="container mx-auto p-6">
      <ToastContainer />
      {/* Header */}
      <div className="flex flex-wrap gap-4 mb-6 items-end">
        <h1 className="text-2xl font-semibold text-gray-800">Collector List</h1>
        <div className="ml-auto flex gap-4 items-end flex-wrap">
          {role_name === "super admin" && (
            <div className="flex flex-col w-[320px]">
              <label className="text-xs text-gray-500 mb-1 font-medium">
                Select Municipality <span className="text-red-400">*</span>
              </label>
              <CustomSelect
                value={municipalty}
                onChange={(e: any) => handleMunicipalityChange(e.target.value)}
                options={municipalityOptions}
                placeholder="Select Municipality"
              />
            </div>
          )}
          <div className="flex flex-col w-[220px]">
            <label className="text-xs text-gray-500 mb-1 font-medium">Choose Type</label>
            <div className="relative">
              <select
                value={selectedRole}
                onChange={(e) => { setSelectedRole(e.target.value); setPage(1); setSearchQuery(""); }}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm appearance-none bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400 pr-8"
              >
                {roleTypes.map((r) => (
                  <option key={r.primary_role_id} value={r.role_name}>
                    {r.role_name === "counter-collector" ? "Counter Collector" : "Field Collector"}
                  </option>
                ))}
              </select>
              <i className="ri-arrow-down-s-line absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
            </div>
          </div>

          <div className="flex flex-col w-[240px]">
            <label className="text-xs text-gray-500 mb-1 font-medium">Search</label>
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none" />
              <input
                type="text"
                placeholder="Name or username..."
                className="w-full border border-gray-200 pl-8 pr-3 py-2.5 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* FIX: Show message for super admin if municipality not selected */}
      {role_name === "super admin" && !municipalty ? (
        <div className="flex flex-col items-center justify-center py-16 bg-white rounded-lg shadow-lg border">
          <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
          <h3 className="text-lg font-medium text-gray-500 mb-2">Select a Municipality</h3>
          <p className="text-sm text-gray-400">Please select a municipality to view collectors</p>
        </div>
      ) : loading ? (
        <div className="flex justify-center items-center py-16">
          <svg className="w-8 h-8 animate-spin text-blue-500" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
        </div>
      ) : (
        <>
          {/* Table */}
          <div className="overflow-x-auto bg-white rounded-lg shadow-lg border">
            <table className="min-w-full text-xs">
              <thead className="bg-gray-100 border-b">
                <tr>
                  <th className="py-2.5 px-3 text-left font-semibold text-gray-600 w-[160px]">Municipality</th>
                  <th className="py-2.5 px-3 text-left font-semibold text-gray-600 w-[140px]">Collector Name</th>
                  <th className="py-2.5 px-3 text-left font-semibold text-gray-600 w-[160px]">Username</th>
                  <th className="py-2.5 px-3 text-left font-semibold text-gray-600 w-[170px]">Email</th>
                  <th className="py-2.5 px-3 text-left font-semibold text-gray-600 w-[90px]">Ward</th>
                  <th className="py-2.5 px-3 text-left font-semibold text-gray-600 w-[90px]">Borough</th>
                  <th className="py-2.5 px-3 text-left font-semibold text-gray-600 w-[120px]">Role</th>
                  <th className="py-2.5 px-3 text-left font-semibold text-gray-600 w-[80px]">Status</th>
                  <th className="py-2.5 px-3 text-left font-semibold text-gray-600 w-[60px]">Report</th>
                  <th className="py-2.5 px-3 text-left font-semibold text-gray-600 w-[60px]">Edit</th>
                </tr>
              </thead>
              <tbody>
                {displayList.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="text-center py-4 text-gray-500 text-sm">
                      {!selectedRole ? "Please select a role" : searchQuery ? "No results found for your search" : "No results found"}
                    </td>
                  </tr>
                ) : (
                  displayList.map((item) => (
                    <tr key={item.primary_id} className="border-b hover:bg-gray-50 transition">
                      <td className="py-2.5 px-3 truncate max-w-[160px]" title={item.municipality_name}>{item.municipality_name}</td>
                      <td className="py-2.5 px-3 truncate max-w-[140px]" title={item.admin_name}>{item.admin_name}</td>
                      <td className="py-2.5 px-3 max-w-[160px]"><span className="block break-all">{item.login_user_name}</span></td>
                      <td className="py-2.5 px-3 truncate max-w-[170px]">{item.email}</td>
                      <td className="py-2.5 px-3">
                        <WardDisplay collector={item} />
                      </td>
                      <td className="py-2.5 px-3 truncate max-w-[90px]">{item.borough_name || "N/A"}</td>
                      <td className="py-2.5 px-3 capitalize">
                        {item.role_name === "collector" ? "Field Collector" : "Counter Collector"}
                      </td>
                      <td className="py-2.5 px-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          item.status === 1 ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                        }`}>
                          {item.status === 1 ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="py-2.5 px-3">
                        <button
                          onClick={() => onViewDetails(item)}
                          title="View Details"
                          className="w-7 h-7 flex items-center justify-center rounded-full text-blue-500 hover:bg-blue-50 hover:text-blue-700 transition"
                        >
                          <i className="ri-eye-line text-base" />
                        </button>
                      </td>
                      <td className="py-2.5 px-3">
                        <button
                          onClick={() => setEditingCollector(item)}
                          title="Edit Collector"
                          className="w-7 h-7 flex items-center justify-center rounded-full text-blue-500 border border-blue-200 hover:bg-blue-50 hover:text-blue-700 transition"
                        >
                          <i className="ri-edit-line" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && !searchQuery && (
            <div className="flex justify-center mt-6">
              <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)} color="primary" />
            </div>
          )}
        </>
      )}

      {/* Edit Modal */}
      {editingCollector && (
        <EditModal
          collector={editingCollector}
          roleTypes={roleTypes}
          municipaltyData={municipalty}
          onClose={() => setEditingCollector(null)}
          onUpdated={fetchCollectors}
        />
      )}
    </div>
  );
};
export default CollectorList;